import { Component, OnInit, inject, ViewChild } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

import { LoadingOverlayComponent } from '../../shared/loading-overlay.component';
import { GestionarContactosInformeDialogComponent } from '../../shared/contactos/gestionar-contactos-informe-dialog.component';
import { CumplimientosProgramadosFacade } from '../services/cumplimientos-programados.facade';
import { FiltrosCumplimientoComponent } from '../components/filtros-cumplimiento/filtros-cumplimiento.component';
import { FiltrosCumplimiento } from '../components/filtros-cumplimiento/filtros-cumplimiento.model';
import { TablaProgramacionesComponent } from '../components/tabla-programaciones/tabla-programaciones.component';
import { FormularioCumplimientoComponent } from '../components/formulario-cumplimiento/formulario-cumplimiento.component';
import { ResumenRiesgosPotencialesComponent } from '../components/resumen-riesgos-potenciales/resumen-riesgos-potenciales.component';
import {
  ProgramacionPendiente,
  CumplimientoRequest,
  HistorialCumplimiento,
  DatosActaRequest
} from 'src/app/domain/model/cumplimiento/cumplimiento';

// Diálogos de búsqueda (lupas) - Reutilizables
import { BusquedaUnidadAdministrativaDialogComponent } from '../../shared/buscadores/buscador-unidad-administrativa-dialog.component';
import { BusquedaTareaDialogComponent } from '../../common/busquedas/busqueda-tarea-dialog.component';
import { BusquedaAsesorDialogComponent } from '../../common/busquedas/busqueda-asesor-dialog.component';
import { BusquedaAseguradoraDialogComponent } from '../../shared/buscadores/buscador-aseguradora-dialog.component';
import { BusquedaClienteDialogComponent } from '../../common/busquedas/busqueda-cliente-dialog.component';
import { BusquedaCentroTrabajoDialogComponent } from '../../common/busquedas/busqueda-centro-trabajo-dialog.component';

// Diálogos del Historial de Cumplimientos (Legacy: ListarCumplimientosViewImpl.java)
import { HistorialCumplimientosDialogComponent } from '../components/historial-cumplimientos-dialog/historial-cumplimientos-dialog.component';
import { ObservacionesRecomendacionesDialogComponent } from '../components/observaciones-recomendaciones-dialog/observaciones-recomendaciones-dialog.component';

/**
 * Componente Container: Cumplimientos Programados
 *
 * Migrado desde: FilterOrdenesProgViewImpl.java + CreateOrUpdateCumplimientoViewImpl.java
 *
 * Patrón: Container Component (Smart)
 * - Orquesta los componentes dumb
 * - Delega toda la lógica al Facade
 * - Solo se encarga de capturar eventos y conectarlos
 *
 * Estructura:
 * - container/ → Este archivo (smart)
 * - components/ → filtros, tabla, formulario (dumb)
 * - services/ → facade con estado y lógica
 *
 * @author Emerson Rodriguez - Ingeneo
 */
@Component({
  selector: 'app-cumplimientos-programados',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    ToastModule,
    GestionarContactosInformeDialogComponent,
    LoadingOverlayComponent,
    FiltrosCumplimientoComponent,
    ResumenRiesgosPotencialesComponent,
    TablaProgramacionesComponent,
    FormularioCumplimientoComponent,
    // Diálogos de búsqueda (lupas)
    BusquedaUnidadAdministrativaDialogComponent,
    BusquedaTareaDialogComponent,
    BusquedaAsesorDialogComponent,
    BusquedaAseguradoraDialogComponent,
    BusquedaClienteDialogComponent,
    BusquedaCentroTrabajoDialogComponent,
    // Diálogos de historial
    HistorialCumplimientosDialogComponent,
    ObservacionesRecomendacionesDialogComponent
  ],
  providers: [MessageService, CumplimientosProgramadosFacade],
  template: `
    <div class="p-3 sm:p-4 lg:p-6 max-w-7xl mx-auto overflow-x-hidden">
      <p-toast></p-toast>
      <!-- Legacy: gestión de contactos del informe tras guardar un cumplimiento con informe Final -->
      <app-gestionar-contactos-informe-dialog #contactosInforme />
      <app-loading-overlay [blocked]="facade.loading()" />

      <!-- Breadcrumb (Legacy: FilterOrdenesProgViewImpl) -->
      <div
        class="text-sm text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-2"
      >
        <span>Cumplimientos</span>
        <span class="text-gray-400 dark:text-gray-500">»</span>
        <span>Consultar Orientaciones Programadas</span>
        <span class="text-gray-400 dark:text-gray-500">»</span>
        <span class="font-medium text-gray-800 dark:text-gray-100"
          >Consultar Ordenes Programadas</span
        >
      </div>

      <!-- Header -->
      <div class="mb-4">
        <h1 class="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-1">
          Orientaciones Programadas
        </h1>
        <p class="text-gray-600 dark:text-gray-400">
          Gestione los cumplimientos de las programaciones pendientes
        </p>
      </div>

      <!-- Filtros de Búsqueda (Dumb Component) -->
      <app-filtros-cumplimiento
        [oficinas]="facade.oficinas()"
        [loading]="facade.loading()"
        [filtro]="facade.filtro()"
        [fechaInicial]="facade.fechaInicial()"
        [fechaFinal]="facade.fechaFinal()"
        [esAsesor]="facade.esAsesor()"
        (filtroChange)="onFiltroChange($event)"
        (fechaInicialChange)="facade.fechaInicial.set($event)"
        (fechaFinalChange)="facade.fechaFinal.set($event)"
        (buscar)="facade.buscar()"
        (limpiar)="facade.limpiarFiltros()"
        (buscarUnidadAdministrativa)="facade.abrirDialogUnidadAdmin()"
        (buscarTarea)="facade.abrirDialogTarea()"
        (buscarConsultor)="facade.abrirDialogConsultor()"
        (buscarAseguradora)="facade.abrirDialogAseguradora()"
        (buscarCliente)="facade.abrirDialogCliente()"
        (buscarCentroTrabajo)="facade.abrirDialogCentroTrabajo()"
      />

      <!-- Tabla de Programaciones (Dumb Component) -->
      @if (facade.busquedaRealizada()) {
        <app-resumen-riesgos-potenciales
          [loading]="facade.riesgoConteoLoading()"
          [total]="facade.riesgoConteoTotal()"
          [abiertos]="facade.riesgoConteoAbiertos()"
          [cerrados]="facade.riesgoConteoCerrados()"
        />
        <div class="flex justify-end -mt-2 mb-4">
          <a
            routerLink="/riesgos-potenciales"
            class="text-sm text-sura-blue-500 hover:underline dark:text-sura-blue-200"
          >
            Ver seguimiento de riesgos potenciales »
          </a>
        </div>

        <app-tabla-programaciones
          [data]="facade.paginatedData()"
          [loading]="facade.loading()"
          [totalRegistros]="facade.totalRegistros()"
          [currentPage]="facade.currentPage()"
          [totalPages]="facade.totalPages()"
          [pageSize]="facade.pageSize()"
          [registroInicio]="facade.registroInicio()"
          [registroFin]="facade.registroFin()"
          [hasPrevious]="facade.hasPrevious()"
          [hasNext]="facade.hasNext()"
          (seleccionar)="facade.abrirFormularioCumplimiento($event)"
          (cambiarPagina)="onCambiarPagina($event)"
          (cambiarTamanio)="facade.cambiarTamanioPagina($event)"
        />
      }

      <!-- Formulario de Cumplimiento (Dumb Component en Dialog) -->
      <app-formulario-cumplimiento
        #formulario
        [visible]="facade.dialogFormVisible()"
        [programacion]="facade.selectedProgramacion()"
        [saldoActual]="facade.saldoActual()"
        [loading]="facade.formLoading()"
        [esTipoCampo]="facade.esTipoCampo()"
        [esTipoInforme]="facade.esTipoInforme()"
        [requiereJustificacion]="facade.requiereJustificacion()"
        [justificaciones]="facade.justificaciones()"
        [municipioOrigenInicial]="facade.municipioOrigenInicial()"
        [municipioEntregaInicial]="facade.municipioEntregaInicial()"
        [actaError]="facade.actaError()"
        [mostrarBotonReintentar]="facade.mostrarBotonReintentar()"
        [actaGenerando]="facade.actaGenerando()"
        [mostrarBotonDescargarActa]="facade.mostrarBotonDescargarActa()"
        [descargandoActa]="facade.descargandoActa()"
        [soloActa]="facade.soloActaModo()"
        [contratosExentos]="facade.contratosCumplString()"
        (visibleChange)="onDialogVisibleChange($event)"
        (guardar)="onGuardarCumplimiento($event)"
        (cancelar)="onCancelarFormulario()"
        (fechaCumplimientoChange)="facade.verificarCumplimientoTardio($event)"
        (reportarAlertaTemprana)="facade.abrirUrlAlertaTemprana()"
        (verHistorial)="facade.verHistorialCumplimientos()"
        (reintentar)="onReintentarActa($event)"
        (descargarActa)="facade.descargarActaAsociada()"
      />

      <!-- =========================================================== -->
      <!-- DIÁLOGOS DE BÚSQUEDA (LUPAS) - Replica Legacy con lupas -->
      <!-- =========================================================== -->

      <!-- Búsqueda Unidad Administrativa -->
      @if (facade.mostrarDialogUnidadAdmin()) {
        <app-busqueda-unidad-administrativa-dialog
          [nmconsecutivoCliente]="facade.filtro().aseguradora?.id"
          (unidadAdministrativaSelected)="facade.seleccionarUnidadAdmin($event)"
          (dialogClosed)="facade.cerrarDialogUnidadAdmin()"
        >
        </app-busqueda-unidad-administrativa-dialog>
      }

      <!-- Búsqueda Tarea -->
      @if (facade.mostrarDialogTarea()) {
        <app-busqueda-tarea-dialog
          (onSelect)="facade.seleccionarTarea($event)"
          (onClose)="facade.cerrarDialogTarea()"
        >
        </app-busqueda-tarea-dialog>
      }

      <!-- Búsqueda Consultor/Asesor -->
      @if (facade.mostrarDialogConsultor()) {
        <app-asesor-search
          (onSelect)="facade.seleccionarConsultorDesdeDialog($event)"
          (onClose)="facade.cerrarDialogConsultor()"
        >
        </app-asesor-search>
      }

      <!-- Búsqueda Aseguradora -->
      @if (facade.mostrarDialogAseguradora()) {
        <app-busqueda-aseguradora-dialog
          (aseguradoraSelected)="facade.seleccionarAseguradora($event)"
          (dialogClosed)="facade.cerrarDialogAseguradora()"
        >
        </app-busqueda-aseguradora-dialog>
      }

      <!-- Búsqueda Cliente/Beneficiario -->
      @if (facade.mostrarDialogCliente()) {
        <app-busqueda-cliente-dialog
          (onSelect)="facade.seleccionarCliente($event)"
          (onClose)="facade.cerrarDialogCliente()"
        >
        </app-busqueda-cliente-dialog>
      }

      <!-- Búsqueda Centro de Trabajo (requiere cliente seleccionado) -->
      @if (facade.mostrarDialogCentroTrabajo()) {
        <app-busqueda-centro-trabajo-dialog
          [nmconsecutivoBeneficiario]="facade.filtro().cliente?.id ?? 0"
          (onSelect)="facade.seleccionarCentroTrabajo($event)"
          (onClose)="facade.cerrarDialogCentroTrabajo()"
        >
        </app-busqueda-centro-trabajo-dialog>
      }

      <!-- =========================================================== -->
      <!-- DIÁLOGOS DE HISTORIAL DE CUMPLIMIENTOS -->
      <!-- Legacy: ListarCumplimientosViewImpl.java -->
      <!-- =========================================================== -->

      <!-- Historial de Cumplimientos Dialog -->
      <app-historial-cumplimientos-dialog
        [visible]="facade.mostrarHistorialDialog()"
        [cumplimientos]="facade.historialCumplimientos()"
        [contratosCumpl]="facade.contratosCumpl()"
        [actaRecienAsociada]="facade.actaRecienAsociada()"
        (cerrar)="facade.cerrarHistorialDialog()"
        (asociarActa)="facade.abrirAsociarActaDesdeHistorial($event)"
        (recargarHistorial)="facade.recargarHistorial()"
        (verObservaciones)="facade.verObservaciones($event)"
      >
      </app-historial-cumplimientos-dialog>

      <!-- Observaciones/Recomendaciones Dialog -->
      <app-observaciones-recomendaciones-dialog
        [visible]="facade.mostrarObservacionesDialog()"
        [cumplimiento]="facade.cumplimientoSeleccionadoObs()"
        (cerrar)="facade.cerrarObservacionesDialog()"
      >
      </app-observaciones-recomendaciones-dialog>
    </div>
  `
})
export class CumplimientosProgramadosComponent implements OnInit {
  readonly facade = inject(CumplimientosProgramadosFacade);
  private readonly route = inject(ActivatedRoute);

  @ViewChild('formulario') formularioRef!: FormularioCumplimientoComponent;
  @ViewChild('contactosInforme') contactosInforme?: GestionarContactosInformeDialogComponent;

  /**
   * Guarda el cumplimiento y, si quedó registrado un informe Final, abre la gestión de contactos
   * del informe (legacy CreateOrUpdateCumplimientoActivity.gestionarContactosInforme).
   */
  async onGuardarCumplimiento(request: CumplimientoRequest): Promise<void> {
    await this.facade.guardarCumplimiento(request);
    const pendiente = this.facade.contactosInformePendiente();
    if (pendiente) {
      this.facade.contactosInformePendiente.set(null);
      await this.contactosInforme?.abrir(pendiente.radicado, pendiente.cumplimientos);
    }
  }

  ngOnInit(): void {
    void this.inicializarVista();
  }

  private async inicializarVista(): Promise<void> {
    await this.facade.cargarCatalogos();

    const queryParams = this.route.snapshot.queryParams as Record<
      string,
      unknown
    >;
    const origen = String(queryParams['origen'] ?? '').trim();
    const nmconsecutivoProgramacion = Number(
      queryParams['nmconsecutivoProgramacion']
    );

    this.facade.setOrigenInformacionTareas(origen === 'informacion-tareas');

    if (origen !== 'informacion-tareas') {
      return;
    }

    if (
      !Number.isFinite(nmconsecutivoProgramacion) ||
      nmconsecutivoProgramacion <= 0
    ) {
      return;
    }

    await this.facade.abrirFormularioCumplimientoPorConsecutivo(
      nmconsecutivoProgramacion
    );
  }

  // Handlers que delegan al facade
  onFiltroChange(filtro: FiltrosCumplimiento): void {
    Object.entries(filtro).forEach(([key, value]) => {
      this.facade.actualizarFiltro(key as any, value);
    });
  }

  onCambiarPagina(accion: 'first' | 'prev' | 'next' | 'last'): void {
    switch (accion) {
      case 'first':
        this.facade.irPrimeraPagina();
        break;
      case 'prev':
        this.facade.irPaginaAnterior();
        break;
      case 'next':
        this.facade.irPaginaSiguiente();
        break;
      case 'last':
        this.facade.irUltimaPagina();
        break;
    }
  }

  onDialogVisibleChange(visible: boolean): void {
    if (!visible && !this.facade.soloActaModo()) {
      this.facade.cerrarFormulario();
    }
  }

  /**
   * Handler del output (cancelar) del formulario-cumplimiento ("Regresar").
   * En modo soloActa: vuelve al historial sin cerrar el diálogo.
   * En modo normal: cierra el formulario.
   */
  onCancelarFormulario(): void {
    if (this.facade.soloActaModo()) {
      this.facade.volverDesdesoloActa();
    } else {
      this.facade.cerrarFormulario();
    }
  }

  /**
   * Handler del output (reintentar) del formulario-cumplimiento.
   * Delega al facade según si está en modo soloActa (desde historial) o reintento post-guardado.
   */
  onReintentarActa(datosActa: DatosActaRequest): void {
    if (this.facade.soloActaModo()) {
      this.facade.onReintentarActaDesdeHistorial(datosActa);
    } else {
      this.facade.onReintentarActa(datosActa);
    }
  }
}
